import {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEvent,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';
import { InputCloseButton } from '../Input/InputCloseButton';
import { SparkIcon } from '../SparkIcon';
import { TagGeneral } from '../Tag/TagGeneral';
import { TooltipTrigger } from '../Tooltip/TooltipTrigger';
import './Search.css';

/** Matches `.spark-search-multi-input { min-width: 48px }` so tags leave room to type. */
const MULTI_SELECT_INPUT_MIN_WIDTH = 48;

/**
 * How many tags fit in `available` px when an overflow chip of `overflowWidth`
 * is reserved. At least one tag is kept when any exist.
 */
export function countVisibleTags(
  tagWidths: number[],
  overflowWidth: number,
  available: number,
  gap: number,
): number {
  if (tagWidths.length === 0) return 0;
  const totalAll = tagWidths.reduce((sum, width, index) => sum + width + (index > 0 ? gap : 0), 0);
  if (totalAll <= available) return tagWidths.length;

  let used = overflowWidth;
  let count = 0;
  for (const width of tagWidths) {
    if (used + width + gap > available) break;
    used += width + gap;
    count += 1;
  }
  return Math.max(count, 1);
}

export type SearchSize = 'small' | 'medium' | 'large';
export type SearchState = 'default' | 'hover' | 'click-on' | 'disabled';
export type SearchFill = 'empty' | 'filled';
export type SearchIconPosition = 'start' | 'end';

/** Search text input with icon, optional clear button, and multi-select tag mode. */
export type SearchProps = {
  size?: SearchSize;
  fill?: SearchFill;
  multiSelect?: boolean;
  placeholder?: string;
  tags?: string[];
  /**
   * Extra items represented by the "+N" chip, added on top of tags that
   * don't fit the field. Multi-select also collapses overflowing tags
   * automatically.
   */
  overflowCount?: number;
  disabled?: boolean;
  visualState?: SearchState;
  showFocus?: boolean;
  showClearButton?: boolean;
  /** Where the search glyph sits. Default `'end'` matches the Spark Search spec. */
  iconPosition?: SearchIconPosition;
  /** When false, the search glyph is omitted (use with `endIcon` for select-style fields). */
  showSearchIcon?: boolean;
  /** Replaces the search glyph on the trailing edge (e.g. InputChevron). */
  endIcon?: ReactNode;
  onClear?: () => void;
  onTagRemove?: (tag: string, index: number) => void;
  className?: string;
} & Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'disabled'>;

export const Search = forwardRef<HTMLInputElement, SearchProps>(function Search({
  size = 'medium',
  fill,
  multiSelect = false,
  placeholder = 'Search',
  tags = [],
  overflowCount = 0,
  disabled = false,
  visualState,
  showFocus = false,
  showClearButton,
  iconPosition = 'end',
  showSearchIcon = true,
  endIcon,
  onClear,
  onTagRemove,
  className = '',
  value,
  defaultValue,
  onChange,
  ...rest
}, ref) {
  const [internalValue, setInternalValue] = useState(
    (value as string | undefined) ?? (defaultValue as string | undefined) ?? '',
  );

  useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value as string);
    }
  }, [value]);

  const currentValue = value !== undefined ? String(value) : internalValue;
  const resolvedFill: SearchFill =
    fill ?? (multiSelect ? (tags.length > 0 ? 'filled' : 'empty') : currentValue.length > 0 ? 'filled' : 'empty');
  const resolvedState: SearchState = disabled ? 'disabled' : visualState ?? 'default';
  const isFocused = showFocus || resolvedState === 'click-on';
  const showClear =
    (showClearButton ?? resolvedFill === 'filled') && !multiSelect && !disabled && currentValue.length > 0;

  const showLeadingSearchIcon = showSearchIcon && iconPosition === 'start' && !endIcon;
  const trailingIcon = endIcon ?? (
    showSearchIcon && iconPosition === 'end' ? (
      <SparkIcon slug="search" size="small" className="spark-search-icon" />
    ) : null
  );

  const rootClasses = [
    'spark-search',
    `spark-search-${size}`,
    multiSelect ? 'spark-search-multi-select' : '',
    `spark-search-fill-${resolvedFill}`,
    `spark-search-state-${isFocused ? 'click-on' : resolvedState}`,
    showLeadingSearchIcon ? 'spark-search-icon-start' : '',
    disabled ? 'spark-search-disabled' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const tagsContainerRef = useRef<HTMLDivElement>(null);
  const tagsMeasureRef = useRef<HTMLDivElement>(null);
  const [visibleTagCount, setVisibleTagCount] = useState(tags.length);

  const updateVisibleTagCount = useCallback(() => {
    if (!multiSelect) return;
    const container = tagsContainerRef.current;
    const measure = tagsMeasureRef.current;
    if (!container || !measure) return;
    const styles = getComputedStyle(container);
    const gap = Number.parseFloat(styles.gap) || 4;
    const available = container.clientWidth - MULTI_SELECT_INPUT_MIN_WIDTH - gap;
    if (available <= 0) return;

    const children = Array.from(measure.children) as HTMLElement[];
    const overflowWidth = children[children.length - 1]?.offsetWidth ?? 0;
    const tagWidths = children.slice(0, -1).map((el) => el.offsetWidth);
    setVisibleTagCount(countVisibleTags(tagWidths, overflowWidth, available, gap));
  }, [multiSelect]);

  const tagsSignature = tags.join('\0');

  useLayoutEffect(() => {
    if (!multiSelect) return undefined;
    updateVisibleTagCount();
    const container = tagsContainerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(() => {
      requestAnimationFrame(updateVisibleTagCount);
    });
    observer.observe(container);
    return () => observer.disconnect();
  }, [multiSelect, tagsSignature, overflowCount, updateVisibleTagCount]);

  const handleClear = () => {
    if (value === undefined) {
      setInternalValue('');
    }
    onClear?.();
    onChange?.({ target: { value: '' } } as ChangeEvent<HTMLInputElement>);
  };

  const shownTagCount = Math.min(visibleTagCount, tags.length);
  const visibleTags = tags.slice(0, shownTagCount);
  const hiddenTags = tags.slice(shownTagCount);
  const totalOverflow = hiddenTags.length + overflowCount;

  return (
    <div className={rootClasses}>
      <div className="spark-search-field">
        {showLeadingSearchIcon ? (
          <SparkIcon slug="search" size="small" className="spark-search-icon" />
        ) : null}
        {multiSelect ? (
          <div className="spark-search-tags" ref={tagsContainerRef}>
            {visibleTags.map((tag, index) => (
              <TagGeneral
                key={`${tag}-${index}`}
                label={tag}
                variant={onTagRemove ? 'closeable' : 'default'}
                onClose={onTagRemove ? () => onTagRemove(tag, index) : undefined}
              />
            ))}
            {totalOverflow > 0 ? (
              hiddenTags.length > 0 ? (
                <TooltipTrigger text={hiddenTags.join(', ')}>
                  <span className="spark-search-tag-overflow-wrap">
                    <TagGeneral
                      label={`+${totalOverflow}`}
                      variant="default"
                      className="spark-search-tag-overflow"
                    />
                  </span>
                </TooltipTrigger>
              ) : (
                <TagGeneral
                  label={`+${totalOverflow}`}
                  variant="default"
                  className="spark-search-tag-overflow"
                />
              )
            ) : null}
            <div className="spark-search-tags-measure" ref={tagsMeasureRef} aria-hidden>
              {tags.map((tag, index) => (
                <TagGeneral
                  key={`measure-${tag}-${index}`}
                  label={tag}
                  variant={onTagRemove ? 'closeable' : 'default'}
                />
              ))}
              <TagGeneral
                label={`+${tags.length + overflowCount}`}
                variant="default"
                className="spark-search-tag-overflow"
              />
            </div>
            <input
              ref={ref}
              className="spark-search-input spark-search-multi-input"
              type="text"
              placeholder={tags.length === 0 ? placeholder : ''}
              disabled={disabled}
              value={value !== undefined ? value : internalValue}
              onChange={(event) => {
                if (value === undefined) {
                  setInternalValue(event.target.value);
                }
                onChange?.(event);
              }}
              aria-label={placeholder}
              {...rest}
            />
          </div>
        ) : (
          <input
            ref={ref}
            className="spark-search-input"
            type="text"
            placeholder={placeholder}
            disabled={disabled}
            value={value !== undefined ? value : internalValue}
            onChange={(event) => {
              if (value === undefined) {
                setInternalValue(event.target.value);
              }
              onChange?.(event);
            }}
            aria-label={placeholder}
            {...rest}
          />
        )}
        {showClear ? (
          <InputCloseButton
            className="spark-search-clear"
            onClick={(event) => {
              event.preventDefault();
              handleClear();
            }}
          />
        ) : null}
        {trailingIcon}
      </div>
    </div>
  );
});

Search.displayName = 'Search';
